import {
  Box,
  Typography,
  Paper,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  CheckCircle,
  Description,
  Security,
  Timeline,
  Business,
  Assignment,
} from "@mui/icons-material";

export default function StartPage({ onStart }) {
  return (
    <Box sx={{ width: "100%", py: 4 }}>
      {/* Header - Modern Design */}
      <Box sx={{ 
        textAlign: "center", 
        mb: 4,
        background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
        borderRadius: 2,
        p: 3,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: "linear-gradient(90deg, #1976d2, #42a5f5, #7b1fa2, #00796b)",
        }
      }}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          sx={{ 
            fontWeight: "700",
            background: "linear-gradient(45deg, #1976d2, #42a5f5)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 1,
            fontSize: { xs: "1.8rem", md: "2.2rem" }
          }}
        >
          GOBD Dokumentations-Generator
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ 
            mb: 3, 
            lineHeight: 1.5,
            fontSize: { xs: "1rem", md: "1.1rem" }
          }}
        >
          Professionelle Dokumentation für Grundsätze ordnungsmäßiger Buchführung
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={onStart}
          sx={{ 
            px: 4, 
            py: 1.5, 
            fontSize: "1rem",
            fontWeight: "600",
            borderRadius: 2,
            background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
            boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
            "&:hover": {
              background: "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)",
              transform: "translateY(-1px)",
              boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
            },
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
          }}
        >
          Dokumentation erstellen
        </Button>
      </Box>

      {/* Verfahrensdokumentation Explanation - Modern Design */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: 2, 
          background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
          border: "1px solid rgba(0,0,0,0.05)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: "linear-gradient(90deg, #1976d2, #42a5f5)",
          }
        }}
      >
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            mb: 2, 
            fontWeight: "600", 
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            fontSize: "1.3rem"
          }}
        >
          <Security sx={{ fontSize: 24 }} />
          Sinn und Zweck dieses Tools
        </Typography>
        <Typography 
          variant="body2" 
          paragraph 
          sx={{ 
            lineHeight: 1.6, 
            fontSize: "0.95rem",
            color: "text.primary",
            mb: 2
          }}
        >
          Dieses Tool unterstützt Sie dabei, eine vollständige und GoBD-konforme Verfahrensdokumentation einfach, strukturiert und praxisnah zu erstellen. Es orientiert sich am offiziellen Muster der AWV (Arbeitsgemeinschaft für wirtschaftliche Verwaltung e. V.), das in Zusammenarbeit mit Wirtschaftskammern und -verbänden im Arbeitskreis „GoB beim IT-Einsatz" entwickelt wurde.
        </Typography>
        <Typography 
          variant="body2" 
          paragraph 
          sx={{ 
            lineHeight: 1.6, 
            fontSize: "0.95rem",
            color: "text.primary",
            mb: 2
          }}
        >
          Ziel ist es, Unternehmen eine verständliche und nachvollziehbare Möglichkeit zu bieten, ihre Prozesse zur Belegablage, Buchführung und Datenaufbewahrung gemäß den GoBD-Anforderungen an Ordnung, Vollständigkeit und Unveränderbarkeit zu dokumentieren.
        </Typography>
        <Typography 
          variant="body2" 
          paragraph 
          sx={{ 
            lineHeight: 1.6, 
            fontSize: "0.95rem",
            color: "text.primary",
            mb: 2
          }}
        >
          Die Verfahrensdokumentation umfasst dabei sämtliche IT-gestützten Datenverarbeitungssysteme und deren Prozessbeschreibungen, die für steuer- und buchführungsrelevante Abläufe von Bedeutung sind.
        </Typography>
        <Box sx={{
          p: 2,
          borderRadius: 1.5,
          background: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
          border: "1px solid rgba(245, 124, 0, 0.2)"
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              lineHeight: 1.6, 
              fontSize: "0.9rem",
              color: "warning.dark",
              fontWeight: "500",
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5
            }}
          >
            <Security sx={{ fontSize: 20, mt: 0.3, flexShrink: 0 }} />
            <span>
              <strong>Wichtiger Hinweis:</strong> Änderungen in Ihrem Unternehmen (z. B. neue Systeme, geänderte Abläufe oder Verantwortlichkeiten) müssen zeitnah in der Verfahrensdokumentation angepasst werden. Alle früheren Versionen sind aufzubewahren, um die Nachvollziehbarkeit sicherzustellen.
            </span>
          </Typography>
        </Box>
      </Paper>

      {/* What is GOBD and Process Overview - Modern Side by Side */}
      <Box sx={{ 
        mb: 4,
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: 3
      }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 2,
                background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                border: "1px solid rgba(0,0,0,0.05)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "3px",
                  background: "linear-gradient(90deg, #1976d2, #42a5f5)",
                }
              }}
            >
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                mb: 2, 
                fontWeight: "600",
                color: "primary.main",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                fontSize: "1.2rem"
              }}
            >
              <Business sx={{ fontSize: 24 }} />
              Was ist GOBD?
            </Typography>
            <Typography 
              variant="body2" 
              paragraph 
              sx={{ 
                lineHeight: 1.6, 
                fontSize: "0.95rem",
                color: "text.primary",
                mb: 1.5
              }}
            >
              Die Grundsätze ordnungsmäßiger Buchführung (GOBD) sind ein zentraler Bestandteil 
              des deutschen Steuerrechts und definieren die Anforderungen an eine ordnungsgemäße 
              Buchführung.
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                lineHeight: 1.6, 
                fontSize: "0.95rem",
                color: "text.primary",
                fontWeight: "500"
              }}
            >
              Eine ordnungsgemäße Dokumentation ist gesetzlich vorgeschrieben und schützt 
              vor steuerlichen Risiken.
            </Typography>
          </Paper>
            <Paper
            elevation={0} 
            sx={{ 
              p: 3, 
              borderRadius: 2,
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              border: "1px solid rgba(0,0,0,0.05)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "3px",
                background: "linear-gradient(90deg, #f57c00, #ffb74d)",
              }
            }}
          >
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                mb: 2, 
                fontWeight: "600",
                color: "warning.main",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                fontSize: "1.2rem"
              }}
            >
              <Timeline sx={{ fontSize: 24 }} />
              Der Dokumentationsprozess
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                lineHeight: 1.6, 
                fontSize: "0.95rem",
                color: "text.primary",
                mb: 2
              }}
            >
              11 strukturierte Schritte führen Sie durch die komplette GOBD-Dokumentation:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {[
                "Unternehmensinfos", "Organisation", "Unterlagen", "Einweisung", "Abgrenzung",
                "Sicherheit", "Zuständigkeiten", "IKS", "Verfahren", "E-Rechnungen", "Freigabe"
              ].map((step, index) => (
                <Typography
                  key={index}
                  variant="caption"
                  sx={{
                    px: 1.5,
                    py: 1,
                    background: "linear-gradient(135deg, #1976d2, #42a5f5)",
                    color: "white",
                    borderRadius: 1.5,
                    fontWeight: "600",
                    display: "inline-block",
                    fontSize: "0.8rem",
                    boxShadow: "0 1px 4px rgba(25, 118, 210, 0.3)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: "0 2px 8px rgba(25, 118, 210, 0.4)",
                    }
                  }}
                >
                  {index + 1}. {step}
                </Typography>
              ))}
            </Box>
          </Paper>
      </Box>

      {/* Features Grid - Modern Layout */}
      <Box sx={{ 
        mb: 4,
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: 3,
        width: "100%"
      }}>
          <Card 
            sx={{ 
              height: "280px",
              width: "100%",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
              },
              borderRadius: 2,
              border: "none",
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "3px",
                background: "linear-gradient(90deg, #1976d2, #42a5f5)",
              }
            }}
          >
            <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}>
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                mb: 2,
                p: 2,
                borderRadius: 1.5,
                background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                boxShadow: "0 2px 8px rgba(25, 118, 210, 0.15)"
              }}>
                <Description sx={{ fontSize: 40, color: "primary.main", mr: 1.5 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: "600", color: "primary.main", mb: 0.5, fontSize: "1.1rem" }}>
                    Vollständige Dokumentation
                  </Typography>
                  <Typography variant="body2" color="primary.dark" sx={{ opacity: 0.8, fontSize: "0.85rem" }}>
                    Umfassende GOBD-Konformität
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: "0.9rem" }}>
                Erstellen Sie eine vollständige und rechtskonforme GOBD-Dokumentation, die alle 
                Anforderungen der Finanzverwaltung erfüllt und Ihr Unternehmen optimal absichert.
              </Typography>
            </CardContent>
          </Card>

          <Card 
            sx={{ 
              height: "280px",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
              },
              borderRadius: 2,
              border: "none",
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "3px",
                background: "linear-gradient(90deg, #f57c00, #ffb74d)",
              }
            }}
          >
            <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}>
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                mb: 2,
                p: 2,
                borderRadius: 1.5,
                background: "linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%)",
                boxShadow: "0 2px 8px rgba(245, 124, 0, 0.15)"
              }}>
                <Timeline sx={{ fontSize: 40, color: "warning.main", mr: 1.5 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: "600", color: "warning.main", mb: 0.5, fontSize: "1.1rem" }}>
                    Schritt-für-Schritt Prozess
                  </Typography>
                  <Typography variant="body2" color="warning.dark" sx={{ opacity: 0.8, fontSize: "0.85rem" }}>
                    Intuitive Benutzerführung
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: "0.9rem" }}>
                Einfacher, strukturierter Prozess mit klaren Anweisungen für jede Phase 
                der Dokumentation. Keine Vorkenntnisse erforderlich - einfach und verständlich.
              </Typography>
            </CardContent>
          </Card>

          <Card 
            sx={{ 
              height: "280px",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
              },
              borderRadius: 2,
              border: "none",
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "3px",
                background: "linear-gradient(90deg, #7b1fa2, #ba68c8)",
              }
            }}
          >
            <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}>
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                mb: 2,
                p: 2,
                borderRadius: 1.5,
                background: "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
                boxShadow: "0 2px 8px rgba(123, 31, 162, 0.15)"
              }}>
                <Business sx={{ fontSize: 40, color: "secondary.main", mr: 1.5 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: "600", color: "secondary.main", mb: 0.5, fontSize: "1.1rem" }}>
                    Unternehmensspezifisch
                  </Typography>
                  <Typography variant="body2" color="secondary.dark" sx={{ opacity: 0.8, fontSize: "0.85rem" }}>
                    Flexible Anpassung
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: "0.9rem" }}>
                Anpassbar an Ihre spezifischen Geschäftsprozesse und Unternehmensstruktur. 
                Flexible Konfiguration für verschiedene Branchen und Unternehmensgrößen.
              </Typography>
            </CardContent>
          </Card>

          <Card 
            sx={{ 
              height: "280px",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
              },
              borderRadius: 2,
              border: "none",
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "3px",
                background: "linear-gradient(90deg, #00796b, #4db6ac)",
              }
            }}
          >
            <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}>
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                mb: 2,
                p: 2,
                borderRadius: 1.5,
                background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
                boxShadow: "0 2px 8px rgba(0, 121, 107, 0.15)"
              }}>
                <Assignment sx={{ fontSize: 40, color: "info.main", mr: 1.5 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: "600", color: "info.main", mb: 0.5, fontSize: "1.1rem" }}>
                    Professionelle Ausgabe
                  </Typography>
                  <Typography variant="body2" color="info.dark" sx={{ opacity: 0.8, fontSize: "0.85rem" }}>
                    Behörden-tauglich
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: "0.9rem" }}>
                Generieren Sie ein professionelles PDF mit korrekter Formatierung, 
                die direkt für Behörden, Prüfer und interne Zwecke geeignet sind.
              </Typography>
            </CardContent>
          </Card>
      </Box>


      {/* Legal Notice - Modern Design */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          background: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
          borderRadius: 2,
          border: "1px solid rgba(245, 124, 0, 0.2)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: "linear-gradient(90deg, #f57c00, #ffb74d)",
          }
        }}
      >
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            fontWeight: "600",
            color: "warning.dark",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            fontSize: "1.2rem"
          }}
        >
          <Security sx={{ fontSize: 24 }} />
          Wichtiger Hinweis
        </Typography>
        <Typography 
          variant="body2" 
          color="warning.dark" 
          sx={{ 
            lineHeight: 1.6,
            fontSize: "0.95rem",
            fontWeight: "500"
          }}
        >
          Dieser Generator unterstützt Sie bei der Erstellung einer GOBD-Dokumentation. 
          Die generierten Dokumente sollten vor der Verwendung von einem Steuerberater 
          oder Rechtsanwalt geprüft werden. Wir übernehmen keine Haftung für die 
          Richtigkeit oder Vollständigkeit der generierten Inhalte ohne eine inhaltliche Prüfung nach der Generierung.
        </Typography>
      </Paper>
    </Box>
  );
} 